import { computed, DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';

/** Los seis cortes de la tabla de `docs/RESPONSIVE.md`. */
export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

/** Un corte y el `min-width` que lo abre. */
interface IBreakpointDef {
  readonly minWidth: number;
  readonly name: Breakpoint;
}

/** Traducción de altura de gráfica por corte (tabla de la §4 del contrato). */
interface IHeightScale {
  readonly factor: number;
  readonly floor: number;
}

/** Un corte activo y su consulta viva. */
interface IBreakpointQuery {
  readonly list: MediaQueryList;
  readonly name: Breakpoint;
}

/** Ascendente: el último que empareja es el corte vigente. */
const BREAKPOINTS: readonly IBreakpointDef[] = [
  { minWidth: 0, name: 'xs' },
  { minWidth: 576, name: 'sm' },
  { minWidth: 768, name: 'md' },
  { minWidth: 1024, name: 'lg' },
  { minWidth: 1280, name: 'xl' },
  { minWidth: 1600, name: 'xxl' },
];

/** Sin `window` no hay ancho que medir; se asume escritorio para no degradar
 *  el tablero en un entorno que ni siquiera va a pintar. */
const FALLBACK: Breakpoint = 'xl';

const HEIGHT_SCALE: Readonly<Record<Breakpoint, IHeightScale>> = {
  xs: { factor: 0.62, floor: 220 },
  sm: { factor: 0.75, floor: 240 },
  md: { factor: 0.88, floor: 260 },
  lg: { factor: 1, floor: 0 },
  xl: { factor: 1, floor: 0 },
  xxl: { factor: 1.12, floor: 0 },
};

/**
 * Corte de pantalla vigente, como señal.
 *
 * Se alimenta de `matchMedia` y no del evento `resize`: el navegador sólo avisa
 * cuando de verdad se cruza un corte, en vez de despertar la detección de
 * cambios en cada píxel mientras el usuario arrastra la ventana.
 */
@Injectable({ providedIn: 'root' })
export class ViewportService {

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /** Una consulta por corte (`xs` no tiene: es el suelo). */
  private readonly queries: readonly IBreakpointQuery[] = this.createQueries();

  private readonly current: WritableSignal<Breakpoint> = signal<Breakpoint>(this.resolve());

  public readonly breakpoint: Signal<Breakpoint> = this.current.asReadonly();

  /** < 768 */
  public readonly isPhone: Signal<boolean> = computed(() => this.rank(this.current()) < this.rank('md'));

  /** 768 – 1023 */
  public readonly isTablet: Signal<boolean> = computed(() => this.current() === 'md');

  /** >= 1024 */
  public readonly isDesktop: Signal<boolean> = computed(() => this.rank(this.current()) >= this.rank('lg'));

  /** >= 1600 */
  public readonly isWide: Signal<boolean> = computed(() => this.current() === 'xxl');

  constructor() {
    this.listen();
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR API PÚBLICA                            */
  /* -------------------------------------------------------------------------- */

  /**
   * Traduce una altura pensada para escritorio al viewport vigente.
   *
   * ECharts con `autoResize` ajusta el ancho pero nunca el alto, así que una
   * banda de 620px se queda igual de alta en un teléfono de 375px.
   */
  public scaleHeight(base: number): number {
    const scale: IHeightScale = HEIGHT_SCALE[this.current()];

    if (scale.factor >= 1) {
      return Math.round(base * scale.factor);
    }

    // El piso sólo levanta lo que el factor hundió por debajo de él: acotar con
    // `min(base, ...)` impide que una gráfica que ya era más baja que el piso
    // crezca (la dona de 200px se queda en 200, no sube a 220).
    return Math.round(Math.min(base, Math.max(scale.floor, base * scale.factor)));
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR INTERNO                              */
  /* -------------------------------------------------------------------------- */

  private createQueries(): readonly IBreakpointQuery[] {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return [];
    }

    return BREAKPOINTS
      .filter((def: IBreakpointDef) => def.minWidth > 0)
      .map((def: IBreakpointDef) => ({
        list: window.matchMedia(`(min-width: ${def.minWidth}px)`),
        name: def.name,
      }));
  }

  private listen(): void {
    for (const query of this.queries) {
      const onChange: () => void = () => this.current.set(this.resolve());

      query.list.addEventListener('change', onChange);
      this.destroyRef.onDestroy(() => query.list.removeEventListener('change', onChange));
    }
  }

  private resolve(): Breakpoint {
    if (this.queries.length === 0) {
      return FALLBACK;
    }

    let match: Breakpoint = 'xs';

    for (const query of this.queries) {
      if (query.list.matches) {
        match = query.name;
      }
    }

    return match;
  }

  private rank(name: Breakpoint): number {
    return BREAKPOINTS.findIndex((def: IBreakpointDef) => def.name === name);
  }
}
